/**
 * API-Client für /api/calendar-entries (Notiz / Frei / Urlaub / Krank / Wunschfrei).
 * Analog zu lib/shifts.ts inkl. Konflikt-Header.
 */

import { apiFetch } from './api';

export type CalendarEntryType = 'note' | 'off' | 'vacation' | 'sick' | 'wish_off';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface ApiCalendarEntry {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  type: CalendarEntryType;
  title: string | null;
  note: string | null;
  halfDay: boolean;
  status: ApprovalStatus;
  createdBy: string | null;
  approvedBy: string | null;
  clientUuid: string | null;
  createdAt: string;
  lastModified: string;
  deletedAt: string | null;
}

export interface ListCalendarEntriesParams {
  from?: string;
  to?: string;
  month?: string;
  userId?: string;
  status?: ApprovalStatus;
}

export interface CreateCalendarEntryInput {
  userId?: string;
  date: string;
  type?: CalendarEntryType;
  title?: string | null;
  note?: string | null;
  halfDay?: boolean;
  clientUuid?: string;
}

export interface UpdateCalendarEntryInput {
  date?: string;
  type?: CalendarEntryType;
  title?: string | null;
  note?: string | null;
  halfDay?: boolean;
  status?: ApprovalStatus;
}

/** Metadaten je Typ: Label, kurzes Kürzel, Farbe für den Kalender. */
export const ENTRY_TYPE_META: Record<
  CalendarEntryType,
  { label: string; short: string; color: string }
> = {
  note: { label: 'Notiz', short: 'Notiz', color: '#64748b' },
  off: { label: 'Frei', short: 'Frei', color: '#0ea5e9' },
  vacation: { label: 'Urlaub', short: 'Urlaub', color: '#f59e0b' },
  sick: { label: 'Krank', short: 'Krank', color: '#ef4444' },
  wish_off: { label: 'Wunschfrei', short: 'Wunsch', color: '#a855f7' },
};

/** Braucht dieser Typ eine Chef-Freigabe? (Notizen nicht.) */
export function needsApproval(type: CalendarEntryType): boolean {
  return type !== 'note';
}

/**
 * Gesamtprüfung 11.09.2026 — spiegelt die Server-Prüfung (PUT
 * /api/calendar-entries/:id): der Chef entscheidet über alle Anträge; eine
 * Filialleitung nur über Anträge ANDERER Personen, deren Haupt-Standort sie
 * verwaltet (ohne markierten Haupt-Standort: irgendeiner ihrer Standorte).
 */
export function canDecideEntry(
  entry: { userId: string },
  ctx: {
    meId: string;
    isOwner: boolean;
    managedLocationIds: string[];
    activeCompanyId: string | null;
    users: { id: string; locationMemberships: { locationId: string; companyId: string | null; isPrimary?: boolean }[] }[];
  },
): boolean {
  if (ctx.isOwner) return true;
  if (entry.userId === ctx.meId) return false;
  const applicant = ctx.users.find((u) => u.id === entry.userId);
  if (!applicant) return false;
  const own = applicant.locationMemberships.filter((m) => m.companyId === ctx.activeCompanyId);
  const primary = own.find((m) => m.isPrimary);
  if (primary) return ctx.managedLocationIds.includes(primary.locationId);
  return own.some((m) => ctx.managedLocationIds.includes(m.locationId));
}

function buildQuery(params: ListCalendarEntriesParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const calendarEntriesApi = {
  list: (token: string, params: ListCalendarEntriesParams = {}) =>
    apiFetch<{ entries: ApiCalendarEntry[] }>(`/api/calendar-entries${buildQuery(params)}`, {
      token,
    }).then((r) => r.entries),

  create: (token: string, data: CreateCalendarEntryInput) =>
    apiFetch<{ entry: ApiCalendarEntry }>('/api/calendar-entries', {
      method: 'POST',
      body: data,
      token,
    }).then((r) => r.entry),

  update: (
    token: string,
    id: string,
    data: UpdateCalendarEntryInput,
    expectedLastModified?: string,
  ) =>
    apiFetch<{ entry: ApiCalendarEntry }>(`/api/calendar-entries/${id}`, {
      method: 'PUT',
      body: data,
      token,
      headers: expectedLastModified ? { 'x-expected-last-modified': expectedLastModified } : undefined,
    }).then((r) => r.entry),

  remove: (token: string, id: string, expectedLastModified?: string) =>
    apiFetch<{ entry: ApiCalendarEntry }>(`/api/calendar-entries/${id}`, {
      method: 'DELETE',
      token,
      headers: expectedLastModified ? { 'x-expected-last-modified': expectedLastModified } : undefined,
    }).then((r) => r.entry),
};
